import React, { useContext, useEffect, useState } from "react";
import { Grid, Card, Box, Stack, Avatar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Calendar, Users, Layers } from "lucide-react";

import { ConnectionContext, UserContext, LanguageContext } from "App";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import UsersApi from "Classes/API/UsersApi";

// Sub-components
import {
  WelcomeHeader,
  QuickAction,
  SectionLabel,
} from "./DashboardComponents";
import AdminDashboardView from "./AdminDashboardView";
import "./Dashboard.css";

// ─── Chart Config Constants ──────────────────────────────────────────────────
const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#ffffff",
      titleColor: "#111827",
      bodyColor: "#64748b",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      padding: 12,
      displayColors: false,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#94a3b8", font: { size: 11 } },
    },
    y: {
      grid: { color: "#f1f5f9", drawBorder: false },
      ticks: { color: "#94a3b8", precision: 0, font: { size: 11 } },
      beginAtZero: true,
    },
  },
};

const lineChartOptions = {
  ...baseChartOptions,
  plugins: {
    ...baseChartOptions.plugins,
    legend: {
      display: true,
      position: "top",
      labels: {
        color: "#334155",
        font: { size: 12, weight: "600" },
        boxWidth: 12,
        padding: 16,
      },
    },
  },
};

const initialHistogramData = {
  labels: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  datasets: [
    {
      label: "Users Per Month",
      data: Array(12).fill(0),
      backgroundColor: "#3560ad",
      borderRadius: 6,
      borderSkipped: false,
    },
  ],
};

const initialChartData = {
  labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
  datasets: [
    {
      label: "New Patients",
      data: Array(24).fill(0),
      borderColor: "#3560ad",
      tension: 0.4,
      fill: true,
      backgroundColor: "rgba(53,96,173,0.07)",
      pointRadius: 0,
      pointHoverRadius: 5,
      borderWidth: 2.5,
    },
    {
      label: "Acc Sessions for the Day",
      data: Array(24).fill(0),
      fill: false,
      borderColor: "#8b5cf6",
      tension: 0.4,
      pointRadius: 0,
      pointHoverRadius: 5,
      borderWidth: 2.5,
    },
  ],
};

function Dashboard() {
  const navigate = useNavigate();
  const User = useContext(UserContext);
  const { translations } = useContext(LanguageContext);
  const Connection = useContext(ConnectionContext);

  const [histogramData, setHistogramData] = useState(initialHistogramData);
  const [chartData, setChartData] = useState(initialChartData);
  const [currentTime, setCurrentTime] = useState("");

  // New State for consolidated highlights
  const [highlights, setHighlights] = useState({
    sessionsToday: 0,
    sessionsThisMonth: 0,
    totalUsers: 0,
    totalPatients: 0,
  });

  const handleOnClick = (url) => navigate(url, { replace: true });

  // Clock Timer
  useEffect(() => {
    const tick = () =>
      setCurrentTime(
        new Date().toLocaleString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  // API Data Fetching
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [highlightsRes, monthRes, dailyRes] = await Promise.all([
          UsersApi.GetDashboardHighlights(), // Your new consolidated endpoint
          UsersApi.GetUsersPerMonth(),
          UsersApi.GetDailyStats(),
        ]);

        // 1. Set Highlights
        if (highlightsRes?.status === "Success") {
          setHighlights(highlightsRes.data);
        }

        // 2. Set Monthly Histogram
        if (monthRes?.data && monthRes.status === "Success") {
          const monthly = Array(12).fill(0);
          monthRes.data.forEach((m) => {
            if (m.month >= 1 && m.month <= 12) monthly[m.month - 1] = m.count;
          });
          setHistogramData((prev) => ({
            ...prev,
            datasets: [{ ...prev.datasets[0], data: monthly }],
          }));
        }

        // 3. Set Daily 24h Chart
        if (dailyRes?.status === "Success") {
          const patients = Array(24).fill(0);
          const sessions = Array(24).fill(0);
          dailyRes.data.newPatients?.forEach((p) => {
            if (p.hour >= 0 && p.hour < 24) patients[p.hour] = p.count;
          });
          dailyRes.data.sessions?.forEach((s) => {
            if (s.hour >= 0 && s.hour < 24) sessions[s.hour] = s.count;
          });
          setChartData((prev) => ({
            ...prev,
            datasets: [
              { ...prev.datasets[0], data: patients },
              { ...prev.datasets[1], data: sessions },
            ],
          }));
        }
      } catch (e) {
        console.error("Error fetching dashboard data", e);
      }
    };
    fetchStats();
  }, []);

  if (User.isActivated === false) return <div>You are not active</div>;
  if (User.isSoftDeleted === true) return <div>You are soft deleted</div>;

  const GetId = () => {
    switch (User.userType) {
      case "Owner":
      case "HeadAccounting":
      case "Admin":
        return (
          <>
            <b>User Type:</b> Admin || <b>ID:</b> {User.id}
          </>
        );
      case "SubAdmin":
        return (
          <>
            <b>User Type:</b> Sub Admin || <b>ID:</b> {User.id}
          </>
        );
      case "AppointmentEmployee":
      case "CallCenterEmployee":
      case "AccountingEmployee":
        return (
          <>
            <b>User Type:</b> {User.userType} || User ID: {User.id}, EmployeeId:{" "}
            {User.getEmployee?.id}
          </>
        );
      default:
        return (
          <>
            <b>User Type:</b> {User.userType || "Unknown"}
          </>
        );
    }
  };

  const renderContent = () => {
    if (["Admin", "Owner", "HeadAccounting"].includes(User.userType)) {
      return (
        <AdminDashboardView
          User={User}
          currentTime={currentTime}
          chartData={chartData}
          histogramData={histogramData}
          lineChartOptions={lineChartOptions}
          baseChartOptions={baseChartOptions}
          translations={translations}
          handleOnClick={handleOnClick}
          highlights={highlights} // Pass highlights to the view
        />
      );
    }

    if (User.userType === "Therapist" || User.userType === "SubAdmin") {
      return (
        <MDBox>
          <WelcomeHeader
            User={User}
            showId={User.userType === "SubAdmin"}
            currentTime={currentTime}
          />
          <SectionLabel icon={Layers}>
            {User.userType === "Therapist" ? "Your Workspace" : "Quick Access"}
          </SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <QuickAction
                title={translations.DashBoardAppointments}
                icon={Calendar}
                color="var(--accent-orange)"
                onClick={() => handleOnClick("/Appointments")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <QuickAction
                title={translations.DashBoardPatients}
                icon={Users}
                color="var(--accent-green)"
                onClick={() => handleOnClick("/PatientHistory")}
              />
            </Grid>
          </Grid>
        </MDBox>
      );
    }

    return (
      <Card className="user-card">
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Avatar className="user-card__avatar">
            {User.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <MDTypography className="user-card__name">{User.name}</MDTypography>
            <MDTypography className="user-card__meta">{GetId()}</MDTypography>
          </Box>
        </Stack>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox className="dashboard-root">{renderContent()}</MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
